import BBOX from 'geojson-bbox'
import proj4 from 'proj4'
import axios from 'axios'

class CollectionPolygonLayer {
	constructor({canvas, maxWidth, features, backgroundFeatures, backgroundPolygon, calculateScale, fontSize, usesScale = false}) {
		this.maxWidth = maxWidth
		this.minX = null
		this.minY = null
		this.maxX = null
		this.maxY = null
		this.height = null
		this.width = null
		this.fontSize = fontSize
		this.fontSizeBuffer = 40

		this.bbox = []
		this.group = []
		this.background = null
		this.backgroundPolygons = null
		this.textGroupLayers = []

		this.calculateBBOX(features, backgroundPolygon, backgroundFeatures)
		this.addFeatures(features)
		this.addBackgroundFeatures(backgroundFeatures)

		if(backgroundPolygon) {
			this.loadBackground(backgroundPolygon).then(() => {
				this.addToCanvas(canvas, {
					scaleWidth: calculateScale({bbox: this.bbox}),
					usesScale: usesScale
				})
			})
		} else {
			this.addToCanvas(canvas, {
				scaleWidth: calculateScale({bbox: this.bbox}),
				usesScale: usesScale
			})
		}
	}

	addFeatures(featureCollection) {

		let coordinates, text

		let polygons = featureCollection.features.map(f => {
			coordinates = this.getPolygonCoordinates(f.geometry)

			text = this.getPolygonText(f)

			this.textGroupLayers.push(text)

			return new fabric.Group([
				new fabric.Polygon(coordinates, {
					fill: f.properties.color,
					strokeWidth: 1,
					stroke: 'gray',
				}),
				text
			])
		})

		this.group = new fabric.Group(polygons)
	}

	addBackgroundFeatures(featureCollection) {
		if(featureCollection) {
			let coordinates

			let polygons = featureCollection.features.map(f => {
				coordinates = this.getPolygonCoordinates(f.geometry)

				return new fabric.Polygon(coordinates, {
					stroke: "black", 
					strokeWidth: 3,
					opacity: 1.0,
					fill: 'rgba(0,0,0,0)'
				});
			})

			this.backgroundPolygons = new fabric.Group(polygons)
		}
	}

	getPolygonText(feature) {
		let coord = proj4('WGS84', 'EPSG:3857', [
			parseFloat(feature.properties.center_lng),
			parseFloat(feature.properties.center_lat)]
		).map((val, index) => {
			return index === 0 ? this.transformX(val) : this.transformY(val)
		})

		let text = new fabric.Text(feature.properties.title, {
			fontSize: this.fontSize + this.fontSizeBuffer,
			fontFamily: 'Helvetica',
			originX: 'center',
			originY: 'center',
			top: coord[1],
			left: coord[0],
			layer_type: 'text'
		});

		let rect = new fabric.Rect({
			originX: 'center',
			originY: 'center',
			top: coord[1],
			left: coord[0],
			fill: 'white',
			width: text.width + 30,
			height: text.height + 20,
			strokeWidth: 2,
			stroke: "gray",
			rx: 10,
			ry: 10,
			layer_type: 'rect'
		});

		return new fabric.Group([rect, text])
	}

	getPolygonCoordinates(geometry) {
		return geometry.coordinates.map(coordinates => {
			return coordinates.map(coordinate => {
				let coord = proj4('WGS84', 'EPSG:3857', coordinate.slice(0,2)).map((val, index) => {
					return index === 0 ? this.transformX(val) : this.transformY(val)
				})
				return {
					x: coord[0],
					y: coord[1]
				}
			})
		})[0]
	}

	transformX(x) {
		return parseInt(x - this.minX)
	}

	transformY(y) {
		return this.height - parseInt(y - this.minY)
	}

	/**
	 * Calculate the minimum and maximum values for X and Y by transforming the 
	 * WGS84 bounding box in to EPSG:3857 coordinate system
	 * Will create bounding boxes for the three different GeoJSON (if they are present)
	 * and will extend the first bbox so that they all are contained in a single bbox
	 */
	calculateBBOX(featureCollection, background, backgroundPolygons) {
		let tmp
		let fcBbox = BBOX(featureCollection)
		let latlng = L.latLngBounds(
			L.latLng(fcBbox[0],fcBbox[1]),
			L.latLng(fcBbox[2],fcBbox[3]),
		)
		if(background) {
			tmp = BBOX(background)
			latlng.extend(L.latLngBounds(
				L.latLng(tmp[0],tmp[1]),
				L.latLng(tmp[2],tmp[3]),
			))
		}
		if(backgroundPolygons) {
			tmp = BBOX(backgroundPolygons)
			latlng.extend(L.latLngBounds(
				L.latLng(tmp[0],tmp[1]),
				L.latLng(tmp[2],tmp[3]),
			))
		}

		this.bbox = [
			latlng._southWest.lat,
			latlng._southWest.lng,
			latlng._northEast.lat,
			latlng._northEast.lng,
		]
		let lowLeft = proj4('WGS84', 'EPSG:3857', [this.bbox[0], this.bbox[1]])
		let topRight = proj4('WGS84', 'EPSG:3857', [this.bbox[2], this.bbox[3]])

		this.minX = lowLeft[0]
		this.minY = lowLeft[1]
		this.maxX = topRight[0]
		this.maxY = topRight[1]
		this.height = this.maxY - this.minY
		this.width = this.maxX - this.minX
	}

	loadBackground(background) {
		let topLeft = [this.bbox[0], this.bbox[3]]
		let bottomRight = [this.bbox[2], this.bbox[1]]
		let requestParams = {
			polygon: JSON.stringify(background.geometry),
			x1: topLeft[0],
			y1: topLeft[1],
			x2: bottomRight[0],
			y2: bottomRight[1]
		}

		this.background = {
			props: {
				topLeft: topLeft,
				bottomRight: bottomRight,
			},
			img: null
		}

		return new Promise((resolve, reject) => {
			axios.post('/print/intersecting/backgroundimage/crop', requestParams)
			.then(response => {
				if(response.data.length > 0) {
					fabric.Image.fromURL(response.data, img => {
						this.background.img = img

						resolve();
					});
				} else {
					axios.post('/print/tiledimage', requestParams)
					.then(response => {
						fabric.Image.fromURL(response.data, img => {
							this.background.img = img
							img.scaleToWidth(this.width)
							img.scaleToHeight(this.height)

							resolve();
						});
					})
				}
			}, (err) => {
				reject(err)
			})
		})
	}

	addToCanvas(canvas, {scaleWidth, usesScale}) {

		if(this.backgroundPolygons) {
			this.group = new fabric.Group([this.backgroundPolygons, this.group])
		}
		if(this.background) {
			this.group = new fabric.Group([this.background.img, this.group])
		}

		if(usesScale) {
			this.group.scaleToWidth(scaleWidth)
			this.group.lockScalingY = true
			this.group.lockScalingX = true
		} else if(this.group.width > this.maxWidth) {
			this.group.scaleToWidth(this.maxWidth)
		}

		let width = this.group.width * this.group.scaleX
		this.group.top = 100
		this.group.left = (this.maxWidth - width) / 2
		canvas.add(this.group);
	}

	/**
	 * Removes the entire layer, then updates the fontSizes
	 * of the text and adjusts their positions
	 * then adds the layer to the canvas again
	 */
	updateFontSize(canvas, fontSize) {
		this.fontSize = fontSize
		canvas.remove(this.group)

		this.textGroupLayers.forEach(g => {
			let text = g.getObjects().find(o => o.layer_type === 'text')
			let rect = g.getObjects().find(o => o.layer_type === 'rect')

			let oldWidth = text.width
			let oldHeight = text.height
			text.set({fontSize: this.fontSize + this.fontSizeBuffer})
			text.set({
				top: text.top + (oldHeight - text.height) / 2,
				left: text.left + (oldWidth - text.width) / 2,
			})
			rect.set({
				top: rect.top + (oldHeight - text.height) / 2,
				left: rect.left + (oldWidth - text.width) / 2,
				width: text.width + 30,
				height: text.height + 20
			})

		})
		canvas.add(this.group)
	}
}

export default CollectionPolygonLayer;